// src/pages/FantaQuizzPage.js
// Orchestratore FantaQuizz: sceglie la modalità, carica i dati (giocatori +
// record/classifiche per modalità), fa da dispatcher tra landing/gioco/
// risultati. La logica di ciascuna modalità vive in src/components/quiz/.
import React, { useCallback, useEffect, useState } from 'react';
import './../components/quiz/quiz.css';
import TopBar from '../components/TopBar';
import { useAuth } from '../context/AuthContext';
import { getPlayersFromFirestore } from '../firebase/playerService';
import { applyFantAppRoleOverrides } from '../utils/fantAppRoleOverrides';
import {
  saveQuizScore,
  getLeaderboard,
  getUserBestScore,
  QUIZ_MODES,
} from '../firebase/quizService';
import { Loader2, Trophy } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ModeSelectLanding from '../components/quiz/ModeSelectLanding';
import SurvivalMode from '../components/quiz/SurvivalMode';
import TimeAttackMode from '../components/quiz/TimeAttackMode';
import GuessPlayerMode, { canPlayGuessMode } from '../components/quiz/GuessPlayerMode';
import ResultsScreen from '../components/quiz/ResultsScreen';
import { pickNextQuestion } from '../components/quiz/quizGenerators';

const EMPTY_MODE_MAP = { survival: null, timeattack: [], guess: [] };

function canStartMode(mode, players) {
  if (mode === 'guess') return canPlayGuessMode(players);
  return Boolean(pickNextQuestion(players));
}

export default function FantaQuizzPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [phase,        setPhase]       = useState('landing'); // landing | game | results
  const [currentMode,  setCurrentMode] = useState(null);
  const [players,      setPlayers]     = useState([]);
  const [quizError,    setQuizError]   = useState(false);
  const [gameRunId,    setGameRunId]   = useState(0);
  const [loadingData,  setLoadingData] = useState(true);
  const [saving,       setSaving]      = useState(false);
  const [leaderboards, setLeaderboards]  = useState({ survival: [], timeattack: [], guess: [] });
  const [personalBests, setPersonalBests] = useState({ survival: null, timeattack: null, guess: null });
  const [result,       setResult]      = useState(null);
  const [loadingScores,setLoadingScores] = useState(true);
  const [scoreError,   setScoreError]  = useState(null);
  const [saveError,    setSaveError]   = useState(null);
  const [saveOutcome,  setSaveOutcome] = useState(null); // { saved, bestScore } | null

  const loadScores = useCallback(async () => {
    const entries = await Promise.all(
      QUIZ_MODES.map(async (mode) => {
        const [lb, pb] = await Promise.all([
          getLeaderboard(10, mode),
          getUserBestScore(user, mode),
        ]);
        return [mode, lb, pb];
      })
    );

    const nextLeaderboards = { ...EMPTY_MODE_MAP };
    const nextBests = { survival: null, timeattack: null, guess: null };
    entries.forEach(([mode, lb, pb]) => {
      nextLeaderboards[mode] = lb;
      nextBests[mode] = pb;
    });
    setLeaderboards(nextLeaderboards);
    setPersonalBests(nextBests);
  }, [user]);

  // Carica dati iniziali: players in priorità, punteggi in background
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // 1. Players dalla cache localStorage — veloci e critici per il quiz
      try {
        const pl = await getPlayersFromFirestore();
        if (!cancelled) {
          // Stesso ruolo classic usato per la sorgente FantApp in asta (vedi
          // StrategyEditPage.js/SimulaRosaPage.js): senza questo, le domande
          // che dipendono dal ruolo (es. "che ruolo gioca X?", o l'esclusione
          // dei portieri dalle domande su gol/assist) userebbero il ruolo
          // grezzo del listone invece di quello corretto per i nomi noti a
          // FANTAPP_ROLE_OVERRIDES_CONFIGURATION.
          setPlayers(applyFantAppRoleOverrides(pl));
          setLoadingData(false);
        }
      } catch (err) {
        console.error('Errore caricamento giocatori:', err);
        if (!cancelled) setLoadingData(false);
      }

      // 2. Punteggi Firebase (3 modalità) — non bloccano il quiz se falliscono
      try {
        await loadScores();
      } catch (err) {
        console.error('Errore caricamento punteggi quiz:', err);
        if (!cancelled) setScoreError(err?.code || err?.message || 'Errore sconosciuto');
      } finally {
        if (!cancelled) setLoadingScores(false);
      }
    })();
    return () => { cancelled = true; };
  }, [loadScores]);

  const handleStart = useCallback((mode) => {
    if (!canStartMode(mode, players)) {
      setQuizError(true);
      setCurrentMode(mode);
      setPhase('game');
      return;
    }
    setQuizError(false);
    setResult(null);
    setSaveOutcome(null);
    setSaveError(null);
    setCurrentMode(mode);
    setGameRunId(id => id + 1);
    setPhase('game');
  }, [players]);

  const handleFinish = useCallback(async (payload) => {
    const mode = payload.mode || currentMode;
    const res = { ...payload, mode };
    setResult(res);
    setPhase('results');
    setSaveOutcome(null);
    setSaveError(null);

    // Non salvare punteggi a zero
    if (res.totalScore <= 0) return;

    setSaving(true);
    try {
      const outcome = await saveQuizScore({
        userId:            user?.uid || 'anonymous',
        displayName:       user?.displayName || user?.email || 'Anonimo',
        email:             user?.email || '',
        mode,
        score:             res.totalScore,
        correctCount:      res.breakdown.filter(b => b.correct).length,
        questionsAnswered: res.breakdown.length,
        durationSecs:      res.totalSecs,
        breakdown:         res.breakdown,
      });
      setSaveOutcome(outcome);

      if (outcome.saved) {
        // Refresh classifica e record solo per la modalità appena giocata
        const [lb, pb] = await Promise.all([
          getLeaderboard(10, mode),
          getUserBestScore(user, mode),
        ]);
        setLeaderboards(prev => ({ ...prev, [mode]: lb }));
        setPersonalBests(prev => ({ ...prev, [mode]: pb }));
      }
    } catch (err) {
      console.error('Errore salvataggio quiz:', err);
      setSaveError(err?.code || err?.message || 'Errore sconosciuto');
    } finally {
      setSaving(false);
    }
  }, [user, currentMode]);

  const handlePlayAgain = useCallback(() => {
    setPhase('landing');
  }, []);

  return (
    <main className="container quiz-page">
      <TopBar />

      <div className="quiz-shell">
        {phase === 'landing' && (
          <ModeSelectLanding
            onStart={handleStart}
            loadingData={loadingData}
            personalBests={personalBests}
            leaderboards={leaderboards}
            loadingScores={loadingScores}
            scoreError={scoreError}
            user={user}
          />
        )}

        {phase === 'game' && quizError && (
          <div className="quiz-panel glass" style={{ textAlign: 'center', padding: 32 }}>
            <p style={{ color: 'var(--muted)', marginBottom: 16 }}>
              Impossibile generare la partita. Assicurati che i dati siano sincronizzati.
            </p>
            <button className="quiz-btn quiz-btn--ghost" onClick={() => setPhase('landing')}>
              ← Torna alla home del quiz
            </button>
          </div>
        )}

        {phase === 'game' && !quizError && currentMode === 'survival' && (
          <SurvivalMode key={gameRunId} players={players} onFinish={handleFinish} />
        )}
        {phase === 'game' && !quizError && currentMode === 'timeattack' && (
          <TimeAttackMode key={gameRunId} players={players} onFinish={handleFinish} />
        )}
        {phase === 'game' && !quizError && currentMode === 'guess' && (
          <GuessPlayerMode key={gameRunId} players={players} onFinish={handleFinish} />
        )}

        {phase === 'results' && result && (
          <ResultsScreen
            mode={result.mode}
            breakdown={result.breakdown}
            totalScore={result.totalScore}
            totalSecs={result.totalSecs}
            extra={{ speedBonus: result.speedBonus }}
            onPlayAgain={handlePlayAgain}
            onHome={() => navigate('/')}
          />
        )}

        {saving && (
          <div className="quiz-saving">
            <Loader2 size={14} className="spin" /> Controllo record e salvataggio...
          </div>
        )}
        {!saving && phase === 'results' && saveOutcome && (
          saveOutcome.saved ? (
            <div className="quiz-saving quiz-saving--ok">
              <Trophy size={14} /> Nuovo record personale! Punteggio salvato.
            </div>
          ) : (
            <div className="quiz-saving">
              Punteggio non salvato: non supera il tuo record di {saveOutcome.bestScore} pt.
            </div>
          )
        )}
        {saveError && (
          <div className="quiz-saving" style={{ background: 'rgba(255,60,60,0.15)', color: 'var(--danger, #f55)' }}>
            Errore salvataggio: {saveError}
          </div>
        )}
      </div>
    </main>
  );
}
